import React, { useCallback, useEffect, useState } from "react";
import { AuthAPI } from "@/lib/api";

/**
 * Compact "Credits used this month" widget for the Studio sidebar.
 * Cyan when healthy, amber at 80%, red at 100%.
 * Refreshes every 60s and on window focus so the designer sees the latest
 * state without a hard refresh.
 */
export default function MyCreditsWidget() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await AuthAPI.myCredits();
      setState(data);
    } catch (e) {
      // Silent failure — widget is non-critical; a 401 etc. should just hide it.
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const tick = setInterval(load, 60_000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(tick);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  if (loading || !state) return null;

  const used = Number(state.used || 0);
  const cap = Number(state.cap || 0);
  const remaining = Math.max(0, cap - used);
  const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
  const color = pct >= 100 ? "#dc2626" : pct >= 80 ? "#F59E0B" : "var(--v-accent)";
  const status =
    pct >= 100 ? "EXHAUSTED" :
    pct >= 80 ? `${Math.round(pct)}% USED` :
    "THIS MONTH";

  return (
    <div className="v-surface" style={{ padding: 20, marginBottom: 20 }} data-testid="my-credits-widget">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div className="overline">Verdict credits</div>
        <div className="mono" style={{ fontSize: 9, color: pct >= 80 ? color : "var(--v-text-2)", letterSpacing: ".08em" }}>
          {status}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 12 }}>
        <span
          style={{ fontSize: 32, fontWeight: 800, color, lineHeight: 1, letterSpacing: "-.02em" }}
          data-testid="my-credits-remaining"
        >
          {remaining.toFixed(2)}
        </span>
        <span className="mono" style={{ color: "var(--v-text-2)", fontSize: 11 }}>
          / {cap.toFixed(2)} cr left
        </span>
      </div>

      <div style={{ height: 3, background: "var(--v-border)", marginTop: 10, borderRadius: 1 }}>
        <div style={{ width: `${pct}%`, height: 3, background: color, transition: "width .3s ease" }} />
      </div>

      <div className="mono" style={{ fontSize: 10, color: "var(--v-text-2)", marginTop: 10, lineHeight: 1.5 }}>
        Used <b style={{ color }}>{used.toFixed(2)} cr</b> in {state.period || "this period"}.
        {pct >= 100 && (
          <span style={{ color, display: "block", marginTop: 4 }}>
            Audits paused until next month or until your studio admin raises your cap.
          </span>
        )}
        {pct >= 80 && pct < 100 && (
          <span style={{ color, display: "block", marginTop: 4 }}>
            Close to your limit — heavier models (Sonnet, Pro) will eat through fast.
          </span>
        )}
      </div>
    </div>
  );
}
